/**
 * Variable substitution for weave operations
 */

import { getExportCleanupMarkers, getMarker } from '../markers.js';

// Matches {{VARIABLE_NAME}} placeholders
// Variable names: start with letter or underscore, followed by alphanumeric or underscore
const VAR_PLACEHOLDER_REGEX = /\{\{([A-Za-z_][A-Za-z0-9_]*)\}\}/g;

// Matches unresolved variables for warning detection
const UNRESOLVED_VAR_REGEX = /\{\{([A-Za-z_][A-Za-z0-9_]*)\}\}/g;

/**
 * Substitute variables in content
 *
 * Replaces all {{VARNAME}} placeholders with their values from the variables record.
 * Variables that don't exist in the record are left unchanged.
 *
 * @param content - The content to process
 * @param variables - Record of variable name to value
 * @returns Object with substituted result and list of unresolved variables
 */
export function substituteVariables(
  content: string,
  variables: Record<string, string>
): { result: string; unresolved: string[] } {
  const unresolved: string[] = [];

  const result = content.replace(VAR_PLACEHOLDER_REGEX, (match, varName: string) => {
    if (varName in variables) {
      return variables[varName];
    }
    // Track unresolved variables
    if (/^[A-Za-z_][A-Za-z0-9_]*$/.test(varName) && !unresolved.includes(varName)) {
      unresolved.push(varName);
    }
    return match; // Leave unchanged
  });

  return { result, unresolved };
}

/**
 * Find all unresolved variables in content (using uppercase convention)
 *
 * @param content - The content to scan
 * @returns Array of unique unresolved variable names
 */
export function findUnresolvedVariables(content: string): string[] {
  const matches = content.match(UNRESOLVED_VAR_REGEX);
  if (!matches) {
    return [];
  }

  // Extract variable names from matches and deduplicate
  const varNames = matches.map(m => m.slice(2, -2)); // Remove {{ and }}
  return [...new Set(varNames)];
}

/**
 * Transform tangle markers in content based on target parameter
 *
 * If target is provided: Replace all <!-- tangle: ANYTHING --> with <!-- tangle: target -->
 * If target is null/empty: Strip all tangle markers entirely (docs-only embed)
 *
 * @param content - The content to transform
 * @param target - The target script name, or null to strip markers
 * @returns Transformed content
 */
export function transformTangleTargets(content: string, target: string | null): string {
  const tangleMarker = getMarker('tangle');
  if (!tangleMarker) return content;

  const normalizedTarget = target
    ? target.split(',').map(part => part.trim()).filter(Boolean).join(',')
    : null;

  if (normalizedTarget) {
    // Replace with new target
    return content.replace(tangleMarker.pattern, `<!-- tangle: ${normalizedTarget} -->`);
  } else {
    // Strip tangle markers entirely
    return content.replace(tangleMarker.pattern, '');
  }
}

/**
 * Clean content for export by removing all leedoc-specific markers
 *
 * Uses the marker registry to strip all markers that shouldn't appear in exported markdown.
 * This includes tangle markers, TOC markers, and any other preview-only syntax.
 *
 * @param content - The content to clean
 * @returns Content with all leedoc-specific markers removed
 */
export function cleanForExport(content: string): string {
  let result = content;

  for (const marker of getExportCleanupMarkers()) {
    // Reset regex lastIndex for global patterns
    marker.pattern.lastIndex = 0;
    result = result.replace(marker.pattern, (...args) => {
      const replacement = marker.cleanForExport(args[0], ...args.slice(1, -2));
      return replacement ?? '';
    });
  }

  return result;
}

/**
 * @deprecated Use cleanForExport instead
 */
export function stripTangleMarkers(content: string): string {
  return cleanForExport(content);
}
